import React, { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import AssistantChat from "@/components/AssistantChat";
import { getAdminToken } from "@/lib/api";
import { useAiContext } from "@/lib/aiContext";

/**
 * Floating bottom-right launcher for the T&J Operations AI assistant.
 * Reads the current page + entity context from AiContextProvider so any
 * question or proposed action is scoped to the record the admin is viewing.
 * Hidden when the user is not authenticated.
 */
export default function FloatingAssistantButton() {
  const [open, setOpen] = useState(false);
  const [hasToken, setHasToken] = useState(!!getAdminToken());
  const { context } = useAiContext();

  useEffect(() => {
    const check = () => setHasToken(!!getAdminToken());
    window.addEventListener("storage", check);
    const id = setInterval(check, 4000);
    return () => { window.removeEventListener("storage", check); clearInterval(id); };
  }, []);

  if (!hasToken) return null;

  const ctxLabel = context?.label || (context?.page ? context.page.charAt(0).toUpperCase() + context.page.slice(1) : null);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full shadow-lg transition-transform hover:scale-105 active:scale-95 pl-2 pr-3 py-2"
        style={{
          background: "linear-gradient(135deg,#0B2545 0%,#1A3A60 100%)",
          color: "#FFFFFF",
          boxShadow: "0 10px 30px rgba(11,37,69,0.35), 0 0 0 4px rgba(212,175,55,0.18)",
        }}
        data-testid="floating-assistant-button"
        aria-label="Open T&J Operations AI Assistant"
      >
        <span className="h-10 w-10 rounded-full grid place-items-center relative"
              style={{ background: "rgba(212,175,55,0.14)" }}>
          <Sparkles className="h-5 w-5" style={{ color: "#D4AF37" }} />
          <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full"
                style={{ background: "#22C55E", boxShadow: "0 0 0 2px #0B2545" }} />
        </span>
        <span className="hidden sm:flex flex-col items-start leading-tight">
          <span className="text-[10px] uppercase tracking-wider" style={{ color: "#FFD774" }}>Ask AI</span>
          <span className="text-[10px]" style={{ color: "rgba(255,255,255,0.75)" }}>
            {ctxLabel ? `on ${ctxLabel}` : "operations"}
          </span>
        </span>
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-md p-0 flex flex-col"
          data-testid="assistant-sheet"
        >
          <SheetHeader className="sr-only">
            <SheetTitle>T&amp;J Operations AI Assistant</SheetTitle>
            <SheetDescription>
              Conversational assistant for leads, revenue, dispatch, supplies, and outreach drafting.
            </SheetDescription>
          </SheetHeader>
          {context ? (
            <div className="px-4 py-2 border-b flex items-center gap-2"
                 style={{ background: "rgba(212,175,55,0.08)", borderColor: "#E2E8F0" }}
                 data-testid="assistant-context-strip">
              <Sparkles className="h-3.5 w-3.5" style={{ color: "#7A5E12" }} />
              <div className="text-[11px]" style={{ color: "#7A5E12" }}>
                Scoped to <b>{ctxLabel || context.entity_type}</b>
                {context.entity_id ? ` · ${context.entity_id.slice(0, 8)}` : ""}
              </div>
            </div>
          ) : null}
          <AssistantChat
            onClose={() => setOpen(false)}
            context={context}
            entityLabel={ctxLabel}
          />
        </SheetContent>
      </Sheet>
    </>
  );
}
